import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

/**
 * VerifiedBadge — the reusable "VERIFIED" pill atom.
 *
 * Communicates that Brainiacs verified a communication channel. The pill shows
 * the verdict word + icon; hover previews the detail card and click PINS it
 * open (second click / outside click / Escape closes). Hover and pin are
 * tracked separately so a click never closes a card hover just opened.
 *
 * The card is portaled to document.body ABOVE the lead modal (z-10200, which
 * clears the modal's z-10010) — stacking contexts inside cards/modals can
 * never clip or cover it.
 */

/** Lazily-created portal root so cards escape overflow-clipped containers. */
const getVerifiedBadgePortal = (): HTMLElement => {
  let root = document.getElementById('verified-badge-tooltip-root');
  if (!root) {
    root = document.createElement('div');
    root.id = 'verified-badge-tooltip-root';
    document.body.appendChild(root);
  }
  return root;
};

/** Must sit above the LeadReportModal overlay (z-10010). */
const CARD_Z = 'fixed z-[10200] w-72 rounded-2xl border border-white/10 bg-[#0B0B0D]/96 p-3.5 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-xl';

export type VerifiedBadgeTone = 'verified' | 'caution' | 'risk';

export interface VerifiedBadgeProps {
  /** Channel icon (Iconify name) shown inside the pill. */
  icon: string;
  /** Pill label — typically 'VERIFIED' or 'RISKY'. */
  label: string;
  /** Tone drives the color: emerald / amber / rose. */
  tone: VerifiedBadgeTone;
  /** Accessible label for the pill button. */
  ariaLabel?: string;
  /** Rich detail card content (hover/click). Omit for a static pill. */
  children?: React.ReactNode;
  /** Extra classes on the pill button. */
  className?: string;
}

const TONE_COLOR: Record<VerifiedBadgeTone, string> = {
  verified: '#10B981',
  caution: '#F59E0B',
  risk: '#EF4444',
};

export const VerifiedBadge: React.FC<VerifiedBadgeProps> = ({
  icon,
  label,
  tone,
  ariaLabel,
  children,
  className = '',
}) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = (hovered || pinned) && Boolean(children);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0, above: true });
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const color = TONE_COLOR[tone];

  // Position the card whenever it opens.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const half = 144 + 8; // half of w-72 + margin
    const left = Math.min(Math.max(rect.left + rect.width / 2, half), window.innerWidth - half);
    // Prefer above; fall back below when too close to the top.
    const above = rect.top > 340;
    setPos({ left, top: above ? rect.top - 8 : rect.bottom + 8, above });
  }, [open]);

  // Close pin on outside click or Escape (hover close is handled on leave).
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (cardRef.current?.contains(e.target as Node)) return;
      setPinned(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPinned(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pinned]);

  const enterPill = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHovered(true);
  };
  const leave = () => {
    // Delayed close so the cursor can travel the gap into the card.
    closeTimer.current = setTimeout(() => setHovered(false), 250);
  };
  const enterCard = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHovered(true);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setPinned((v) => !v);
        }}
        onMouseEnter={enterPill}
        onMouseLeave={leave}
        aria-expanded={open}
        aria-label={ariaLabel ?? label}
        className={`inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full border px-2 py-[3px] text-[9px] font-black uppercase tracking-[0.1em] transition-opacity hover:opacity-80 ${className}`}
        style={{
          color,
          borderColor: `${color}55`,
          backgroundColor: `${color}14`,
        }}
      >
        <Icon icon={icon} className="h-3 w-3 shrink-0" aria-hidden="true" />
        {label}
      </button>

      {open && createPortal(
        <div
          ref={cardRef}
          style={{
            left: pos.left,
            top: pos.top,
            transform: pos.above ? 'translateX(-50%) translateY(-100%)' : 'translateX(-50%)',
            // Invisible bridge covering the gap between pill and card so the
            // cursor never "leaves" while traveling between them.
            ...(pos.above ? { paddingBottom: 16, marginBottom: -16 } : { paddingTop: 16, marginTop: -16 }),
          }}
          className={CARD_Z}
          role="dialog"
          aria-label={`${label} details`}
          onMouseEnter={enterCard}
          onMouseLeave={() => { if (!pinned) leave(); }}
        >
          {children}
        </div>,
        getVerifiedBadgePortal(),
      )}
    </>
  );
};

// ── Channel variants ─────────────────────────────────────────────────────────

/**
 * A channel pill driven purely by its lvl2 `xx_id_enriched` timestamp: value
 * present ⇒ VERIFIED by Brainiacs (hover shows when). No value ⇒ no pill.
 *
 * Shared by WhatsApp, Mobile, Phone, Website, LinkedIn — every channel that
 * goes through the same enrichment verification pipeline.
 */
export interface ChannelVerifiedBadgeProps {
  channel: 'whatsapp' | 'mobile' | 'phone' | 'website' | 'linkedin' | 'sms';
  /** ISO timestamp from the lvl2 `xx_id_enriched` column. */
  enrichedAt?: string | null;
  /** The channel value (phone number, URL…) shown in the hover card. */
  value?: string | null;
}

const CHANNEL_META: Record<ChannelVerifiedBadgeProps['channel'], { icon: string; label: string; field: string }> = {
  whatsapp: { icon: 'mdi:whatsapp', label: 'WhatsApp', field: 'whatsapp' },
  mobile: { icon: 'solar:smartphone-bold-duotone', label: 'Mobile', field: 'mobile' },
  phone: { icon: 'solar:phone-calling-rounded-bold-duotone', label: 'Phone', field: 'phone' },
  website: { icon: 'solar:global-bold-duotone', label: 'Website', field: 'website' },
  linkedin: { icon: 'mdi:linkedin', label: 'LinkedIn', field: 'linkedin' },
  sms: { icon: 'solar:chat-round-dots-bold-duotone', label: 'SMS', field: 'sms' },
};

const formatVerifiedDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

export const ChannelVerifiedBadge: React.FC<ChannelVerifiedBadgeProps> = ({
  channel,
  enrichedAt,
  value,
}) => {
  const meta = CHANNEL_META[channel];
  if (!enrichedAt) return null;

  return (
    <VerifiedBadge
      icon="solar:check-circle-bold-duotone"
      label="VERIFIED"
      tone="verified"
      ariaLabel={`${meta.label} verified by Brainiacs. Click for details.`}
    >
      <div>
        <div className="flex items-center gap-2">
          <Icon icon={meta.icon} className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
          <span className="text-[12px] font-semibold text-emerald-400">{meta.label} verified</span>
        </div>
        {value && <p className="mt-1 break-words text-[12px] font-medium text-white">{value}</p>}
        <p className="mt-0.5 text-[11px] leading-4 text-white/45">
          Brainiacs validated this {meta.label.toLowerCase()} channel during enrichment.
        </p>
        <p className="mt-2 text-[10px] text-emerald-300/80">✓ Verified on {formatVerifiedDate(enrichedAt)}</p>
      </div>
    </VerifiedBadge>
  );
};
